// src/components/ProductCard.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Tooltip as ReactTooltip } from "react-tooltip";
import { ProductCardProps, displayPrice, useHandleAddToCart, Customer, User, Product, Modifier } from '../utils/types'; // Added Modifier
import { useSpring, animated } from 'react-spring';
import { useInView } from 'react-intersection-observer';
import { IconContext } from "react-icons";
import { FaShieldHeart, FaLink } from "react-icons/fa6";
import ProductModal from './ProductModal';
import ModifierModal from './ModifierModal';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useCustomerContext } from '../context/customerContext';
import { hasRole } from '../utils/auth';
import CustomerProfileModal from './CustomerProfileModal'; // Import the modal
import { toast } from 'react-toastify'; // For notifications

// Define a type for the price data expected from the parent
interface MTPriceData {
    price: number;
    role: string; // The role for which this price applies
}

// Update ProductCardProps to include customer data for action check
interface EnhancedProductCardProps extends ProductCardProps {
    mtPriceData: MTPriceData | null | undefined;
    effectiveUserId: number | undefined;
    effectiveUserRole: string | undefined;
    effectiveUserIsAuthenticated: boolean;
    customerForAction: Customer | null; // Receive the full customer object
    isLoadingCustomerForAction: boolean; // Receive loading state
    loadingPrices: boolean; // <-- Add loadingPrices prop

}


const ProductCard: React.FC<EnhancedProductCardProps> = ({
    product,
    mtPriceData,
    effectiveUserId,
    effectiveUserRole,
    effectiveUserIsAuthenticated,
    customerForAction, // Destructure customer data
    isLoadingCustomerForAction, // Destructure loading state
    loadingPrices // <-- Destructure loadingPrices prop

}) => {
    const [showProductModal, setShowProductModal] = useState(false); // Renamed state for clarity
    const { handleAddToCart, showModifierModal, handleCloseModifierModal, modifiers, selectedProduct, handleModifierSelection, handleConfirmModifiers, selectedModifiers } = useHandleAddToCart();
    const { ref, inView } = useInView({ rootMargin: '300px', threshold: 0.1 });
    const [hasImageLoaded, setHasImageLoaded] = useState(false);
    const imageRef = useRef<HTMLImageElement>(null);

    // --- State for Customer Profile Modal ---
    const [showCustomerProfileModal, setShowCustomerProfileModal] = useState(false);

    useEffect(() => {
        if (inView && !hasImageLoaded) setHasImageLoaded(true);
    }, [inView, hasImageLoaded]);

    const props = useSpring({
        transform: inView ? 'scale(1)' : 'scale(0.5)',
        opacity: inView ? 1 : 0.5,
        config: { tension: 100, friction: 20 },
    });

    // Add this inside your ProductCard component
    useEffect(() => {
        if (showCustomerProfileModal) {
            // console.log("useEffect: showCustomerProfileModal is now true. customerForAction:", customerForAction);
        } else {
            // console.log("useEffect: showCustomerProfileModal is now false.");
        }
    }, [showCustomerProfileModal, customerForAction]); // Add customerForAction as dependency too


    // --- Updated Add to Cart Click Handler ---
    const handleAddToCartClick = () => {
        // 1. Check Authentication
        if (!effectiveUserIsAuthenticated) {
            toast.error("Please log in to add items.");
            return;
        }

        // 2. Check if customer data is still loading
        if (isLoadingCustomerForAction) {
            toast.info("Checking customer details...");
            return; // Wait for customer data to load
        }

        // 3. Check if customer data exists and has phone/email
        if (!customerForAction) {
            toast.error("Could not verify customer details. Please try again or contact support.");
            console.error("Add to cart check failed: customerForAction is null or undefined");
            return;
        }

        const hasPhone = customerForAction.phone && String(customerForAction.phone).trim() !== '';
        const hasEmail = customerForAction.email && String(customerForAction.email).trim() !== '';

        if (!hasPhone || !hasEmail) {
            // 4. If missing, show the profile modal
            toast.warn("Please complete your profile (Phone and Email) before adding items.");
            setShowCustomerProfileModal(true);
            // !!! This log runs BEFORE React re-renders with the new state value !!!
            console.log("customerForAction : ", customerForAction, showCustomerProfileModal);
        } else {
            // 5. If present, proceed with adding to cart
            if (imageRef.current) {
                const priceToAdd = mtPriceData?.price ?? product.product_value;
                handleAddToCart(product, imageRef, priceToAdd, effectiveUserId, effectiveUserRole);
            } else {
                console.error('ProductCard: imageRef.current is null or undefined!');
                toast.error("An error occurred. Please try again.");
            }
        }
    };
    // --- End Updated Handler ---

    const handleProductModalOpen = () => setShowProductModal(true);
    const handleProductModalClose = () => setShowProductModal(false);

    // --- Handler to close the Customer Profile Modal ---
    const handleCustomerProfileModalClose = () => {
        setShowCustomerProfileModal(false);
        // Optional: Re-fetch customer data here or rely on parent (Home) to update if needed
        // Consider if the modal itself should trigger a refresh on successful update.
    };

    const truncateProductName = (name: string, maxLength: number) => name.length > maxLength ? name.slice(0, maxLength) + '...' : name;
    const truncateProductDesc = (desc: string | null, maxLength: number) => desc && desc.length > maxLength ? desc.slice(0, maxLength) + '...' : desc;

    function toTitleCase(str: string | undefined) {
        if (!str) return '';
        return str.replace(/_/g, ' ')
            .replace(/\w\S*/g, text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase());
    }

    return (
        <div className="w-1/2 sm:w-1/3 md:w-1/4 lg:w-1/5 border border-b-2 border-b-gray-400 rounded px-1 py-1 mx-0 my-1">
            {/* Image Section */}
            <div className="w-full h-48 md:w-full md:h-48 rounded p-1 flex flex-col justify-center items-center" ref={ref} onClick={handleProductModalOpen}>
                <animated.div style={props}>
                    {hasImageLoaded && (
                        <motion.img
                            layoutId={`product-image-${product.id}`}
                            src={product.media.length > 0 ? product.media[0].preview_url : 'placeholder.jpg'}
                            alt={product.product_name}
                            className="product-image px-1 py-1 h-40 md:h-46 object-cover"
                            loading="lazy"
                            ref={imageRef}
                        />
                    )}
                </animated.div>
            </div>

            {/* Add to Cart Button */}
            {effectiveUserIsAuthenticated && (
                <div className="add2cart-div">
                    <button
                        onClick={handleAddToCartClick}
                        // Disable button while checking customer data
                        disabled={isLoadingCustomerForAction}
                        className={`add2cart-btn ${isLoadingCustomerForAction ? 'opacity-50 cursor-wait' : ''}`}
                    >
                        {isLoadingCustomerForAction ? 'Checking...' : 'Add to Cart'}
                    </button>
                </div>
            )}

            {/* Modifier Modal */}
            {showModifierModal && selectedProduct && (
                <ModifierModal
                    product={selectedProduct}
                    modifiers={modifiers}
                    onClose={handleCloseModifierModal}
                    onModifierSelection={handleModifierSelection}
                    onConfirm={handleConfirmModifiers}
                    selectedModifiers={selectedModifiers}
                />
            )}

            {/* Product Info */}
            <div className="min-h-16 md:min-h-16 rounded px-2 py-0" >
                <p className="text-xs text-gray-500 font-thin" >{product.cat_name}</p>
                <h3
                    className="text-sm tracking-[0.08em] text-black font-bold subpixel-antialiased"
                    style={{ fontSize: '0.9rem', }}
                    data-tooltip-id={`name-${product.id}`}
                    data-tooltip-content={product.product_name}
                >
                    {truncateProductName(product.product_name, 68)}
                </h3>
                <ReactTooltip id={`name-${product.id}`} place="bottom" style={{ backgroundColor: "yellow", color: "#000000", fontSize: '1rem', zIndex: 50 }} />
            </div>
            <div className="min-h-20 rounded px-2 py-2" >
                <p className="text-sm tracking-[0.01em] text-gray-700 font-normal break-all" style={{ fontSize: '0.8rem', }} data-tooltip-id={`desc-${product.id}`} data-tooltip-content={product.product_desc || ''}>
                    {truncateProductDesc(product.product_desc, 95)}
                </p>
                {product.product_desc && product.product_desc.length > 95 && <ReactTooltip id={`desc-${product.id}`} place="bottom" style={{ backgroundColor: "green", color: "#FFFFFF", fontSize: '1em', zIndex: 50 }} />}
            </div>
            <div className="min-h-6 rounded px-2 py-0" ref={ref} onClick={handleProductModalOpen}>
                <p className="mt-0 text-blue-600 font-normal break-all" style={{ fontSize: '0.8em' }}>SKU: {product.product_code}</p>
            </div>


            {/* Price Display */}
            <div className="price-div text-right rounded px-2 py-0 ">


                {/* --- Skeleton Loading for Price --- */}
                {loadingPrices && effectiveUserIsAuthenticated && (
                    <div className="animate-pulse flex justify-end items-baseline mb-1">
                        <div className="h-3 bg-gray-200 rounded w-12 mr-1"></div>
                        <div className="h-5 bg-gray-200 rounded w-16"></div>
                    </div>
                )}
                {/* --- End Skeleton Loading --- */}


                {effectiveUserIsAuthenticated && mtPriceData ? (
                    <p className="text-xs text-green-600 font-semibold">
                        {/* {toTitleCase(mtPriceData.role)}: <span className='font-bold text-lg'>{displayPrice(mtPriceData.price)}</span> */}
                        VIP: <span className='font-bold text-lg'>{displayPrice(mtPriceData.price)}</span>
                    </p>
                ) : effectiveUserIsAuthenticated ? (
                    <p className="text-xs text-gray-500">No specific price yet.</p>
                ) : null}
                <p className="my-1 py-0 text-xs text-black">SRP <span className='font-bold text-lg'>{displayPrice(product.product_value)}</span></p>
            </div>



            {/* Product Modal */}
            {showProductModal && (
                <ProductModal
                    product={product}
                    onClose={handleProductModalClose}
                    // --- Pass the missing props down ---
                    customerForAction={customerForAction}
                    isLoadingCustomerForAction={isLoadingCustomerForAction}
                    mtPriceData={mtPriceData}
                    effectiveUserId={effectiveUserId}
                    effectiveUserRole={effectiveUserRole}
                    effectiveUserIsAuthenticated={effectiveUserIsAuthenticated}
                // --- End passing props ---
                />
            )}

            {/* Stock/Warranty/Link Info */}
            <div className="flex flex-row justify-between mt-2 ">
                {product?.stock !== undefined && product?.stock > 0 ? <span className="productCardStock px-2 py-1 my-1 text-white bg-green-600 border rounded-lg shadow ">In Stock</span> : <span className="productCardStock px-2 py-1 my-1 text-white bg-orange-600 border rounded-lg shadow">Back Order</span>}
                {product?.restock !== undefined && product?.restock > 0 && <span className="productCardStock px-2 py-1 my-1 text-white bg-purple-600 border rounded-lg shadow" >Re-Stock{product?.restock_at && <> on {new Date(product.restock_at).toLocaleDateString()}</>}</span>}
                <div className='flex flex-row justify-between items-center mt-2'>
                    {product.product_link && <a href={product.product_link} className='mr-2' target="_blank" rel="noreferrer" data-tooltip-id={`link-${product.id}`} data-tooltip-content={`External ${product.product_name} Product Details Link`}><IconContext.Provider value={{ size: "1em", color: "purple" }}><FaLink /></IconContext.Provider></a>}
                    {product.product_link && <ReactTooltip id={`link-${product.id}`} place="top" style={{ backgroundColor: "yellow", color: "#000000", zIndex: 50 }} />}
                    {product.warranty && <div data-tooltip-id={`warranty-${product.id}`} data-tooltip-content={`${product.warranty} warranty`}><IconContext.Provider value={{ size: "1em", color: "brown" }}><FaShieldHeart /></IconContext.Provider></div>}
                    {product.warranty && <ReactTooltip id={`warranty-${product.id}`} place="top" style={{ backgroundColor: "yellow", color: "#000000", zIndex: 50 }} />}
                </div>
            </div>

            {/* --- Render Customer Profile Modal Conditionally --- */}
            {(() => {
                // Add this temporary log
                if (showCustomerProfileModal) {
                    console.log("Rendering check: showCustomerProfileModal=true, customerForAction=", customerForAction);
                }
                // Original rendering logic
                return showCustomerProfileModal && customerForAction && (

                    // <div style={{ position: 'fixed', top: '30%', left: '30%', width: '40%', height: '40%', background: 'lightblue', border: '2px solid red', zIndex: 9999 }}>
                    //     MODAL SHOULD BE HERE!
                    //     <button onClick={handleCustomerProfileModalClose}>Close Simple</button>
                    // </div>

                    // <div style={{ position: 'fixed', top: '5%', left: '0%', width: '100%', zIndex: 9999 }}>
                    <CustomerProfileModal

                        initialCustomerData={customerForAction}
                        onClose={handleCustomerProfileModalClose}
                    />
                    // </div>
                );
            })()}
            {/* --- End Customer Profile Modal --- */}
        </div>
    );
};

export default ProductCard;
